/**
 * Persistent HUD header — visual port of the vanilla `topnav` from
 * `_legacy/mc.html` lines 43-77 + `_legacy/xaes.css` lines 252-369.
 *
 * Layout (3 columns, 56 px tall):
 *
 *   ┌─ left (flex 1, justify-start) ─────────────────────────────────────┐
 *   │ DXC logo · divider · [CLIENT mark · name] · divider ·             │
 *   │                   ● Mission Control / WS4 …                       │
 *   │                     XAES PHASE ZERO                               │
 *   └────────────────────────────────────────────────────────────────────┘
 *
 *   ┌─ centre (absolute, 50 % horizontal) ────────────────────────────────┐
 *   │  [pill: role]   [pill: ●ACTIVE]   07:00:00                         │
 *   │                                    SESSION ENDS                    │
 *   └────────────────────────────────────────────────────────────────────┘
 *
 *   ┌─ right (flex 1, justify-end, gap 10 px) ───────────────────────────┐
 *   │   MC:   XAES-AI · Meet Crew · 🔒 · 🌙 · Logout                    │
 *   │   WD:   ← Mission Control   · 🔒 · 🌙 · Logout                    │
 *   └────────────────────────────────────────────────────────────────────┘
 *
 * Functional changes vs vanilla:
 *   - Logo click → `/` (vanilla used `XAES.toDefault()` — same intent).
 *   - "Meet Crew" → opens `MeetCrewModal` for everyone. The MANAGE CREW
 *     tab only appears when power-user mode is unlocked.
 *   - 🔒 toggles power-user mode via `PowerUserModal` (legacy: "Admin Login").
 *   - 🌙 / ☀ swap via `next-themes` (legacy: `toggleMode()`).
 *   - Logout calls `useAuth().logout`.
 *   - XAES-AI button is currently a no-op — the React build mounts the
 *     XAES Analyst permanently in the right column on MC + WD, so there's
 *     no separate modal to open. Kept for visual parity with legacy.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { ArrowLeft, Bot, Check, CheckCheck, Library, Lock, LockOpen, LogOut, Menu, Moon, Pause, Play, RotateCcw, Search, Sparkles, Sun, User as UserIcon, Users, Workflow } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DxcLogo } from "@/components/icons/DxcLogo";
import { ClientBadge } from "@/features/client/ClientBadge";
import { usePowerUser } from "@/stores/usePowerUser";
import { useXaesAgentModal } from "@/stores/useXaesAgentModal";
import { useWorkshopModals } from "@/features/workshop/modals/useWorkshopModals";
import { useAuth } from "@/hooks/use-auth";
import { useCountdown, type TimerUrgency } from "@/hooks/use-countdown";
import { WORKSHOPS, type Workshop, type WorkshopStatus } from "@/content/workshops";
import { useProgress, resolveStatus } from "@/stores/useProgress";
import { setWorkshopComplete } from "@/lib/workshop-completion";
import { login as loginContent } from "@/content/ui";
import { PowerUserModal } from "@/components/shell/PowerUserModal";
import { MeetCrewModal } from "@/components/shell/MeetCrewModal";
import { XaesAgentModal } from "@/components/shell/XaesAgentModal";
import { KeyboardShortcutsOverlay } from "@/components/shell/KeyboardShortcutsOverlay";

function timerColor(urgency: TimerUrgency): string {
  if (urgency === "critical") return "var(--accent-red)";
  if (urgency === "warning") return "var(--accent-gold)";
  return "var(--text-primary)";
}

function useActiveLabel(): { display: string; full: string } {
  const { pathname } = useLocation();
  const match = /^\/workshop\/([^/]+)$/.exec(pathname);
  if (match) {
    const id = match[1] ?? "";
    const ws = WORKSHOPS.find((w) => w.id === id);
    if (!ws) return { display: id, full: id };
    // Short nav title in the HUD (fits the bar); full longTitle on hover.
    const short = ws.title.replace(/\n/g, " ");
    return {
      display: `${ws.id} · ${short}`,
      full: ws.longTitle,
    };
  }
  return { display: "Mission Control", full: "Mission Control" };
}

/** Pulsing green status dot reused in three places — header session,
 *  ACTIVE pill, page-label row. Mirrors the inline `<span style="…">` the
 *  vanilla uses verbatim. */
function StatusDot({ size = 7 }: { size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: "50%",
        background: "var(--accent-green)",
        boxShadow: "var(--glow-green)",
        animation: "pulse 2s infinite",
        display: "inline-block",
      }}
    />
  );
}

/* Band accent per workshop (Color Principle #2 — true-blue WS1/2, sky
   WS3/4, gold WS5-7, melon WS8). Same lookup shape the Mission Control
   WorkshopPanel uses, so the in-session sign-off pill wears the exact
   colour its pre-entry twin does. */
const BAND_ACCENT_VAR: Record<Workshop["accent"], string> = {
  "true-blue": "var(--accent-true-blue)",
  sky: "var(--accent-sky)",
  gold: "var(--accent-gold)",
  melon: "var(--accent-melon)",
  peach: "var(--accent-peach)",
};
const BAND_ACCENT_RGB: Record<Workshop["accent"], string> = {
  "true-blue": "var(--accent-true-blue-rgb)",
  sky: "var(--accent-sky-rgb)",
  gold: "var(--accent-gold-rgb)",
  melon: "var(--accent-melon-rgb)",
  peach: "var(--accent-peach-rgb)",
};

/* Centre status-pill body. On a workshop route it reflects THAT
   workshop's status (so it stops being a hardcoded literal and gives
   live feedback when the sign-off toggle fires); everywhere else it is
   the live-session "ACTIVE" signal. */
function StatusPillBody({
  status,
  accentVar,
}: {
  status: WorkshopStatus;
  accentVar: string;
}) {
  if (status === "done") {
    return (
      <>
        <Check
          aria-hidden
          strokeWidth={2.5}
          style={{ width: 13, height: 13, color: accentVar }}
        />
        <span style={{ color: accentVar, fontWeight: 700 }}>COMPLETE</span>
      </>
    );
  }
  if (status === "locked") {
    return (
      <>
        <span
          aria-hidden
          style={{
            flexShrink: 0,
            width: 8,
            height: 8,
            borderRadius: "50%",
            border: "1.5px solid var(--text-muted)",
            display: "inline-block",
          }}
        />
        <span style={{ color: "var(--text-muted)", fontWeight: 700 }}>
          UPCOMING
        </span>
      </>
    );
  }
  return (
    <>
      <StatusDot size={8} />
      <span style={{ color: "var(--accent-green)", fontWeight: 700 }}>ACTIVE</span>
    </>
  );
}

export function HudHeader() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const workshopMatch = /^\/workshop\/([^/]+)$/.exec(pathname);
  const isWorkshopPage = workshopMatch !== null;
  const currentWorkshopId = workshopMatch?.[1] ?? null;
  const { resolvedTheme, setTheme } = useTheme();
  const isLight = resolvedTheme === "light";
  const isPowerUnlocked = usePowerUser((s) => s.isUnlocked);
  const lockPowerUser = usePowerUser((s) => s.lock);
  const { user, logout } = useAuth();
  const {
    display: timer,
    urgency,
    running: timerRunning,
    toggle: toggleTimer,
    reset: resetTimer,
  } = useCountdown();
  const { display: activeLabel, full: activeLabelFull } = useActiveLabel();
  // Current workshop + its live status (override ?? static default),
  // read from the SAME store the navigator badge derives from — no
  // second derivation path, so the badge, this pill and the sign-off
  // toggle can never disagree.
  const workshopStatusMap = useProgress((s) => s.workshopStatus);
  const currentWorkshop =
    (currentWorkshopId
      ? WORKSHOPS.find((w) => w.id === currentWorkshopId)
      : null) ?? null;
  const workshopStatus: WorkshopStatus = currentWorkshop
    ? resolveStatus(workshopStatusMap, currentWorkshop.id, currentWorkshop.status)
    : "active";
  const workshopDone = workshopStatus === "done";
  const bandAccentVar = currentWorkshop
    ? BAND_ACCENT_VAR[currentWorkshop.accent]
    : "var(--accent-true-blue)";
  const bandAccentRgb = currentWorkshop
    ? BAND_ACCENT_RGB[currentWorkshop.accent]
    : "var(--accent-true-blue-rgb)";
  // Centre pill reflects the workshop on /workshop/:id; the live-session
  // "ACTIVE" signal everywhere else.
  const centerStatus: WorkshopStatus =
    isWorkshopPage && currentWorkshop ? workshopStatus : "active";
  const [powerOpen, setPowerOpen] = useState(false);
  const [crewOpen, setCrewOpen] = useState(false);
  const agentOpen = useXaesAgentModal((s) => s.isOpen);
  const openAgent = useXaesAgentModal((s) => s.open);
  const closeAgent = useXaesAgentModal((s) => s.close);
  // Track A launcher → opens the cross-cutting knowledge surfaces (mounted
  // by the workshop's ModalTriggers). Workshop view only.
  const openWorkshopModal = useWorkshopModals((s) => s.open);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const toggleShortcuts = useCallback(() => setShortcutsOpen((v) => !v), []);

  // Open the Repository overlay, remembering where we came from so closing
  // returns there. From a workshop, deep-link pre-filtered to it.
  const openRepository = useCallback(() => {
    const target = currentWorkshopId
      ? `/repository?ws=${currentWorkshopId}`
      : "/repository";
    navigate(target, { state: { from: pathname + search } });
  }, [navigate, currentWorkshopId, pathname, search]);

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      // Cmd/Ctrl+K opens the Repository overlay (search-first) from anywhere,
      // even a text field — the universal "jump to search" gesture.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        if (pathname === "/repository") return; // already open
        e.preventDefault();
        openRepository();
        return;
      }
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "?") {
        e.preventDefault();
        toggleShortcuts();
      }
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [toggleShortcuts, openRepository, pathname]);

  const handlePowerClick = () => {
    if (isPowerUnlocked) lockPowerUser();
    else setPowerOpen(true);
  };

  /* Inline-style helpers — keeps the JSX readable. Mirrors legacy CSS
     (.topnav, .nav-divider, .nav-session-title, .nav-pill, .nav-btn,
     .nav-btn-icon, .nav-btn-logout) one-for-one. */

  const dividerStyle: React.CSSProperties = {
    width: 1,
    height: 28,
    background: "hsl(var(--border))",
    margin: "0 16px",
    flexShrink: 0,
  };

  // RT-AGENDA — shared chrome for the session-timer Pause/Resume + Reset
  // icon buttons (power-user mode only).
  const timerCtrlBtnStyle: React.CSSProperties = {
    background: "none",
    border: 0,
    padding: 2,
    cursor: "pointer",
    color: "var(--text-muted)",
    display: "inline-flex",
    alignItems: "center",
  };

  const sessionTitleStyle: React.CSSProperties = {
    fontSize: "var(--fs-title)",
    fontWeight: 700,
    color: "var(--text-primary)",
    letterSpacing: "0.04em",
    display: "flex",
    alignItems: "center",
    gap: 7,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  };

  const sessionSubStyle: React.CSSProperties = {
    fontSize: "var(--fs-label)",
    color: "var(--text-muted)",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  /* `display` deliberately lives on the element's `flex` class, not here:
     the narrow-screen rules in index.css hide these with `display: none`,
     which an inline style would win against. Same for `.hud-center`,
     `.hud-session` and `.hud-bar` below. */
  const pillStyle: React.CSSProperties = {
    alignItems: "center",
    gap: 7,
    fontSize: "var(--fs-label)",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--text-secondary)",
    pointerEvents: "auto",
  };

  /* Workshop sign-off toggle — the in-session twin of the Mission
     Control "Mark Workshop Complete" button. Identical vocabulary (band
     accent, 1.5px tracking, CheckCheck / RotateCcw); the only sanctioned
     difference is the inline-pill shape that fits the 56px bar. Routes
     through the same setWorkshopComplete cascade, so the centre pill and
     navigator badge flip in real time. */
  const workshopToggleBase: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    flexShrink: 0,
    padding: "5px 12px",
    borderRadius: 8,
    fontSize: "var(--fs-label)",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "all 0.2s",
  };
  const workshopToggleStyle: React.CSSProperties = workshopDone
    ? {
        ...workshopToggleBase,
        background: "transparent",
        border: "1px solid hsl(var(--border))",
        color: "var(--text-muted)",
      }
    : {
        ...workshopToggleBase,
        background: `rgba(${bandAccentRgb}, 0.04)`,
        border: `1px solid rgba(${bandAccentRgb}, 0.3)`,
        color: bandAccentVar,
      };

  const navBtnStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    border: "1px solid hsl(var(--border))",
    background: "var(--bg-card)",
    color: "var(--text-secondary)",
    fontSize: "var(--fs-base)",
    fontWeight: 600,
    letterSpacing: "0.06em",
    cursor: "pointer",
    borderRadius: 8,
    transition:
      "transform var(--duration-press) var(--ease-out-expo), border-color var(--duration-state) var(--ease-out-expo), color var(--duration-state) var(--ease-out-expo), background var(--duration-state) var(--ease-out-expo)",
  };

  const navBtnIconStyle: React.CSSProperties = {
    width: 28,
    height: 28,
    border: "1px solid hsl(var(--border))",
    background: "var(--bg-card)",
    color: "var(--text-secondary)",
    fontSize: "var(--fs-sub)",
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition:
      "transform var(--duration-press) var(--ease-out-expo), border-color var(--duration-state) var(--ease-out-expo), color var(--duration-state) var(--ease-out-expo), background var(--duration-state) var(--ease-out-expo)",
    position: "relative",
  };

  const navBtnIconUnlockedStyle: React.CSSProperties = {
    ...navBtnIconStyle,
    border: "1px solid var(--accent-gold)",
    color: "var(--accent-gold)",
    boxShadow: "var(--glow-orange)",
  };

  const navBtnLogoutStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "5px 14px",
    border: "1px solid hsl(var(--border))",
    background: "var(--bg-card)",
    color: "var(--text-secondary)",
    fontSize: "var(--fs-base)",
    fontWeight: 600,
    letterSpacing: "0.06em",
    cursor: "pointer",
    borderRadius: 8,
  };

  /* Hover swaps border + colour to accent-true-blue, matching legacy
     `.nav-btn:hover` / `.nav-btn-icon:hover` / `.nav-btn-logout:hover`. */
  const onHover = (color: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.borderColor = color;
    e.currentTarget.style.color = color;
  };
  const onUnhover = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.borderColor = "hsl(var(--border))";
    e.currentTarget.style.color = "var(--text-secondary)";
  };

  return (
    <>
      <nav
        role="banner"
        className="hud-bar"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 40,
          display: "flex",
          alignItems: "center",
          gap: 0,
          height: 56,
          background: "var(--bg-panel)",
          borderBottom: "1px solid hsl(var(--border))",
          flexShrink: 0,
          backdropFilter: "blur(14px) saturate(1.15)",
          WebkitBackdropFilter: "blur(14px) saturate(1.15)",
        }}
      >
        {/* LEFT — equal-flex lane (logo + divider + session block, plus the
            power-mode sign-off toggle). `minWidth:0` + `overflow:hidden` keep
            its content inside the lane so the title ellipsizes and nothing can
            spill under the centre cluster. Both side lanes use the same
            `flex:1 1 0`, so they're always equal width and the centre column
            stays geometrically centred regardless of either side's content. */}
        <div
          style={{
            flex: "1 1 0",
            display: "flex",
            alignItems: "center",
            gap: 0,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <button
            type="button"
            aria-label="Go to Mission Control"
            onClick={() => navigate("/")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "transparent",
              border: 0,
              padding: 0,
              cursor: "pointer",
              // This is the "go home" affordance, so it must survive the
              // left lane shrinking rather than being clipped away.
              flexShrink: 0,
            }}
          >
            <DxcLogo height={28} />
          </button>

          {/* Multi-client workspace identity — strong callout immediately
              after DXC so "which client" outranks workshop title when
              scanning the HUD. Renders null (no orphan divider) when
              multi-client is off or the user can't switch. */}
          <ClientBadge />

          <div className="hud-session-divider" style={dividerStyle} aria-hidden />
          <div
            className="hud-session flex"
            style={{
              flexDirection: "column",
              gap: 1,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <div style={sessionTitleStyle} title={activeLabelFull}>
              <StatusDot size={7} />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {activeLabel}
              </span>
            </div>
            <div className="hud-session-sub" style={sessionSubStyle}>
              XAES PHASE ZERO
            </div>
          </div>

          {/* Workshop sign-off — sits right of the workshop identity it is
              scoped to, reading "you are in WS4 -> sign off WS4". Gated to
              workshop routes so Mission Control's pre-entry button stays
              the sole home there. RT-GATING: requires power-user (admin)
              mode — program state is only mutable in admin mode, like the
              Run Sequence toggle and Manage Crew. */}
          {isWorkshopPage && currentWorkshop && isPowerUnlocked && (
            <>
              <div style={dividerStyle} aria-hidden />
              <button
                type="button"
                aria-pressed={workshopDone}
                title={
                  workshopDone
                    ? "Reopen this workshop"
                    : "Mark this workshop complete"
                }
                style={workshopToggleStyle}
                onMouseEnter={(e) => {
                  if (workshopDone) return;
                  e.currentTarget.style.borderColor = bandAccentVar;
                }}
                onMouseLeave={(e) => {
                  if (workshopDone) return;
                  e.currentTarget.style.borderColor = `rgba(${bandAccentRgb}, 0.3)`;
                }}
                onClick={() =>
                  setWorkshopComplete(currentWorkshop.id, !workshopDone)
                }
              >
                {workshopDone ? (
                  <>
                    <RotateCcw
                      aria-hidden
                      style={{ width: 13, height: 13 }}
                      strokeWidth={2}
                    />
                    Reopen
                  </>
                ) : (
                  <>
                    <CheckCheck
                      aria-hidden
                      style={{ width: 13, height: 13 }}
                      strokeWidth={2}
                    />
                    Mark complete
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {/* CENTRE — in-flow but `flexShrink:0`, so it never compresses and,
            sitting between two equal-flex lanes, stays dead-centre. Reserving
            its own width this way is what keeps the side buttons out of the
            clock — no absolute positioning or magic side caps needed. */}
        <div
          className="hud-center flex"
          style={{
            flexShrink: 0,
            alignItems: "center",
          }}
        >
          <div className="hud-role-pill flex" style={pillStyle}>
            <UserIcon
              aria-hidden
              style={{
                width: 14,
                height: 14,
                color: "var(--accent-gold)",
              }}
            />
            <span style={{ color: "var(--accent-gold)" }}>{user?.role ?? "—"}</span>
          </div>

          <div className="hud-status-pill flex" style={pillStyle}>
            <StatusPillBody status={centerStatus} accentVar={bandAccentVar} />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pointerEvents: "auto",
            }}
          >
            {/* RT-AGENDA — the session clock is an interactive facilitator
                timer in power-user (admin) mode: explicit Pause/Resume +
                Reset controls. Everyone else sees the read-only display. */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  fontSize: "var(--fs-heading)",
                  color:
                    isPowerUnlocked && !timerRunning
                      ? "var(--text-muted)"
                      : timerColor(urgency),
                  letterSpacing: "0.06em",
                  fontVariantNumeric: "tabular-nums",
                  transition: "color 1s",
                }}
              >
                {timer}
              </div>
              {isPowerUnlocked && (
                <>
                  <button
                    type="button"
                    onClick={toggleTimer}
                    aria-label={
                      timerRunning ? "Pause session timer" : "Resume session timer"
                    }
                    title={timerRunning ? "Pause session timer" : "Resume session timer"}
                    style={timerCtrlBtnStyle}
                  >
                    {timerRunning ? (
                      <Pause aria-hidden style={{ width: 13, height: 13 }} />
                    ) : (
                      <Play aria-hidden style={{ width: 13, height: 13 }} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetTimer}
                    aria-label="Reset session timer"
                    title="Reset session timer"
                    style={timerCtrlBtnStyle}
                  >
                    <RotateCcw aria-hidden style={{ width: 13, height: 13 }} />
                  </button>
                </>
              )}
            </div>
            <div
              className="hud-timer-caption"
              style={{
                fontSize: "var(--fs-label)",
                color: "var(--text-muted)",
                letterSpacing: "0.08em",
                textAlign: "center",
                marginTop: 1,
              }}
            >
              {isPowerUnlocked && !timerRunning ? "PAUSED" : "SESSION ENDS"}
            </div>
          </div>
        </div>

        {/* RIGHT — flex:1, justify-end. Action cluster: XAES-AI · Meet
            Crew · 🔒 · theme · Logout. The Phase 6 sync indicator used
            to live here as a 28 px icon button (`SyncStatusPill`), but
            was moved into the AssetLocker panel footer so the status
            sits next to the data it describes. The component is still
            exported from `SyncStatusPill.tsx` for any caller that wants
            the standalone affordance back. */}
        <div
          className="hud-right-lane flex"
          style={{
            minWidth: 0,
            justifyContent: "flex-end",
            overflow: "hidden",
          }}
        >
          <div className="hud-actions flex" style={{ alignItems: "center" }}>
            {isWorkshopPage ? (
              /* WD variant — Repository (search + browse) + the
                 "← Mission Control" back button, which replaces the
                 XAES-AI + Meet Crew pair (legacy `_legacy/wd.html` lines
                 57-62). Same `.nav-btn` visual contract. */
              <>
                {/* Track A knowledge surfaces (append-only launcher group,
                    per the collision matrix): cross-workshop search, the
                    traceability graph, and the MC-level Insight Library. */}
                <button
                  type="button"
                  aria-label="Search All"
                  className="xaes-press hud-nav-btn hud-collapsible"
                  style={navBtnStyle}
                  onMouseEnter={onHover("var(--accent-true-blue)")}
                  onMouseLeave={onUnhover}
                  onClick={() => openWorkshopModal("cross-search")}
                  title="Search every workshop's records at once"
                >
                  <Search aria-hidden style={{ width: 13, height: 13 }} />
                  <span className="nav-label-wd">Search All</span>
                </button>
                <button
                  type="button"
                  aria-label="Traceability"
                  className="xaes-press hud-nav-btn hud-collapsible"
                  style={navBtnStyle}
                  onMouseEnter={onHover("var(--accent-true-blue)")}
                  onMouseLeave={onUnhover}
                  onClick={() => openWorkshopModal("traceability")}
                  title="Trace why a decision was made — links across workshops"
                >
                  <Workflow aria-hidden style={{ width: 13, height: 13 }} />
                  <span className="nav-label-wd">Traceability</span>
                </button>
                <button
                  type="button"
                  aria-label="Insights"
                  className="xaes-press hud-nav-btn hud-collapsible"
                  style={navBtnStyle}
                  onMouseEnter={onHover("var(--accent-true-blue)")}
                  onMouseLeave={onUnhover}
                  onClick={() => openWorkshopModal("insight-library")}
                  title="Insight Library — curated rollup across all 8 workshops"
                >
                  <Sparkles aria-hidden style={{ width: 13, height: 13 }} />
                  <span className="nav-label-wd">Insights</span>
                </button>
                <button
                  type="button"
                  aria-label="Repository"
                  className="xaes-press hud-nav-btn hud-collapsible"
                  style={navBtnStyle}
                  onMouseEnter={onHover("var(--accent-true-blue)")}
                  onMouseLeave={onUnhover}
                  onClick={openRepository}
                  title="Repository — search & browse documents and this workshop's records (Ctrl/Cmd+K)"
                >
                  <Library aria-hidden style={{ width: 13, height: 13 }} />
                  <span className="nav-label-wd">Repository</span>
                </button>
                <button
                  type="button"
                  aria-label="Mission Control"
                  title="Back to Mission Control"
                  className="xaes-press hud-nav-btn"
                  style={navBtnStyle}
                  onMouseEnter={onHover("var(--accent-true-blue)")}
                  onMouseLeave={onUnhover}
                  onClick={() => navigate("/")}
                >
                  <ArrowLeft aria-hidden style={{ width: 13, height: 13 }} />
                  <span className="nav-label-wd-back">Mission Control</span>
                </button>
              </>
            ) : (
              <>
                {/* XAES-AI — opens info card modal (legacy parity). Live
                    chat lives in the page sidebar; this window is the
                    "About the analyst" surface ported from
                    `_legacy/mc.html` MC_AGENT. */}
                <button
                  type="button"
                  aria-label="XAES-AI"
                  title="About the XAES analyst"
                  className="xaes-press hud-nav-btn hud-collapsible"
                  style={navBtnStyle}
                  onMouseEnter={onHover("var(--accent-true-blue)")}
                  onMouseLeave={onUnhover}
                  onClick={openAgent}
                >
                  <Bot aria-hidden style={{ width: 13, height: 13 }} />
                  <span className="nav-label-mc">XAES-AI</span>
                </button>

                <button
                  type="button"
                  aria-label="Meet Crew"
                  title="Meet the crew"
                  className="xaes-press hud-nav-btn hud-collapsible"
                  style={navBtnStyle}
                  onMouseEnter={onHover("var(--accent-true-blue)")}
                  onMouseLeave={onUnhover}
                  onClick={() => setCrewOpen(true)}
                >
                  <Users aria-hidden style={{ width: 13, height: 13 }} />
                  <span className="nav-label-mc">Meet Crew</span>
                </button>

                {/* Repository — the unified area: tier-scoped documents +
                    AI search, plus everything workshops capture. Every crew
                    member can at least browse the shared library, so this is
                    shown to all signed-in users; the BFF enforces per-tier
                    read/write/delete and search scope server-side. */}
                <button
                  type="button"
                  aria-label="Repository"
                  className="xaes-press hud-nav-btn hud-collapsible"
                  style={navBtnStyle}
                  onMouseEnter={onHover("var(--accent-true-blue)")}
                  onMouseLeave={onUnhover}
                  onClick={openRepository}
                  title="Repository — search & browse documents and workshop records (Ctrl/Cmd+K)"
                >
                  <Library aria-hidden style={{ width: 13, height: 13 }} />
                  <span className="nav-label-mc">Repository</span>
                </button>
              </>
            )}

            {/* Admin / power-user lock toggle */}
            <button
              type="button"
              aria-label={isPowerUnlocked ? "Lock admin mode" : "Admin login"}
              title={isPowerUnlocked ? "Lock admin mode" : "Admin login"}
              className="hud-icon-btn hud-collapsible"
              style={isPowerUnlocked ? navBtnIconUnlockedStyle : navBtnIconStyle}
              onMouseEnter={(e) => {
                if (isPowerUnlocked) return;
                e.currentTarget.style.borderColor = "var(--accent-true-blue)";
                e.currentTarget.style.color = "var(--accent-true-blue)";
              }}
              onMouseLeave={(e) => {
                if (isPowerUnlocked) return;
                e.currentTarget.style.borderColor = "hsl(var(--border))";
                e.currentTarget.style.color = "var(--text-secondary)";
              }}
              onClick={handlePowerClick}
            >
              {isPowerUnlocked ? (
                <LockOpen aria-hidden style={{ width: 14, height: 14 }} />
              ) : (
                <Lock aria-hidden style={{ width: 14, height: 14 }} />
              )}
            </button>

            {/* Theme toggle */}
            <button
              type="button"
              aria-label={isLight ? "Switch to dark mode" : "Switch to light mode"}
              title={isLight ? "Switch to dark mode" : "Switch to light mode"}
              className="hud-icon-btn"
              style={navBtnIconStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--accent-true-blue)";
                e.currentTarget.style.color = "var(--accent-true-blue)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "hsl(var(--border))";
                e.currentTarget.style.color = "var(--text-secondary)";
              }}
              onClick={() => setTheme(isLight ? "dark" : "light")}
            >
              {isLight ? (
                <Moon aria-hidden style={{ width: 14, height: 14 }} />
              ) : (
                <Sun aria-hidden style={{ width: 14, height: 14 }} />
              )}
            </button>

            {user && (
              <button
                type="button"
                aria-label={loginContent.logoutLabel}
                title={loginContent.logoutLabel}
                className="hud-logout-btn"
                style={navBtnLogoutStyle}
                onMouseEnter={onHover("var(--accent-true-blue)")}
                onMouseLeave={onUnhover}
                onClick={logout}
              >
                {/* Icon and label swap at the phone tier (`.nav-icon-logout` /
                    `.nav-label-logout` in index.css) so the button keeps its
                    text shape on desktop and still fits a 375px bar. */}
                <LogOut
                  aria-hidden
                  className="nav-icon-logout"
                  style={{ width: 14, height: 14 }}
                />
                <span className="nav-label-logout">{loginContent.logoutLabel}</span>
              </button>
            )}

            {/* Phone-tier collapse — below 560px the icon row above (5-6
                buttons) crowds the 56px bar against the logo/client mark.
                `.hud-collapsible` hides those buttons at that tier (CSS);
                this single trigger takes their place with the same
                actions in a dropdown, so nothing is lost, just tucked
                away. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Open menu"
                  title="Menu"
                  className="hud-icon-btn hud-mobile-menu-trigger"
                  style={navBtnIconStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--accent-true-blue)";
                    e.currentTarget.style.color = "var(--accent-true-blue)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "hsl(var(--border))";
                    e.currentTarget.style.color = "var(--text-secondary)";
                  }}
                >
                  <Menu aria-hidden style={{ width: 16, height: 16 }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8}>
                {isWorkshopPage ? (
                  <>
                    <DropdownMenuItem onClick={() => openWorkshopModal("cross-search")}>
                      <Search aria-hidden style={{ width: 14, height: 14, marginRight: 8 }} />
                      Search All
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openWorkshopModal("traceability")}>
                      <Workflow aria-hidden style={{ width: 14, height: 14, marginRight: 8 }} />
                      Traceability
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openWorkshopModal("insight-library")}>
                      <Sparkles aria-hidden style={{ width: 14, height: 14, marginRight: 8 }} />
                      Insights
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openRepository}>
                      <Library aria-hidden style={{ width: 14, height: 14, marginRight: 8 }} />
                      Repository
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={openAgent}>
                      <Bot aria-hidden style={{ width: 14, height: 14, marginRight: 8 }} />
                      XAES-AI
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setCrewOpen(true)}>
                      <Users aria-hidden style={{ width: 14, height: 14, marginRight: 8 }} />
                      Meet Crew
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openRepository}>
                      <Library aria-hidden style={{ width: 14, height: 14, marginRight: 8 }} />
                      Repository
                    </DropdownMenuItem>
                  </>
                )}
                {/* Theme + Logout stay as always-visible icon buttons at
                    every tier (see the buttons above) — frequent /
                    "always reachable" actions, not tucked away here. */}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handlePowerClick}>
                  {isPowerUnlocked ? (
                    <LockOpen aria-hidden style={{ width: 14, height: 14, marginRight: 8 }} />
                  ) : (
                    <Lock aria-hidden style={{ width: 14, height: 14, marginRight: 8 }} />
                  )}
                  {isPowerUnlocked ? "Lock admin mode" : "Admin login"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Mounted as siblings so they portal outside the sticky header */}
      <PowerUserModal open={powerOpen} onClose={() => setPowerOpen(false)} />
      <MeetCrewModal open={crewOpen} onClose={() => setCrewOpen(false)} />
      <XaesAgentModal open={agentOpen} onClose={closeAgent} />
      <KeyboardShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </>
  );
}
